<template>
  <div>
    <h1 class="text-3xl font-bold mb-6 text-center text-gray-800">3. Take Pictures with Phone</h1>
    <div class="flex justify-center space-x-4 mb-6">
      <button @click="goToText" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition">
        Use Text Instead
      </button>
      <button @click="goToUpload" class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition">
        Upload from Computer
      </button>
    </div>

    <!-- QR Code Section -->
    <div class="flex justify-center items-center mb-6 bg-gray-50 p-4 rounded-lg border">
      <img v-if="qrCode" :src="qrCode" alt="QR Code" class="w-48 h-48 mr-6"/>
      <div v-else class="w-48 h-48 bg-gray-200 flex items-center justify-center mr-6">Loading...</div>
      <div class="text-left">
        <h3 class="font-semibold text-lg text-gray-800">Scan & Snap</h3>
        <p class="text-gray-600 mt-2">
          Use your phone's camera to scan this QR code. It will open a secure page allowing you to take pictures of the question sheets and upload them directly.
        </p>
      </div>
    </div>

    <questions-pages-overview ref="overview"/>

    <!-- START: Added Navigation Buttons -->
    <div class="flex justify-between mt-8">
      <button
          @click="goBack"
          class="bg-gray-500 text-white px-4 py-2 rounded transition hover:bg-gray-600 flex items-center min-w-[120px] justify-center"
          :disabled="loading"
      >
        Back
      </button>

      <button
          @click="triggerNext"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center min-w-[120px] justify-center"
          :disabled="loading">
        <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z"></path>
        </svg>
        <span v-if="loading">Processing...</span>
        <span v-else>{{ buttonText }}</span>
      </button>
    </div>
    <!-- END: Added Navigation Buttons -->
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '../services/axiosInstance'
import QuestionsPagesOverview from '@/components/QuestionsPagesOverview.vue'

const qrCode = ref(null)
const overview = ref(null)
const router = useRouter()
const route = useRoute()
const loading = ref(false) // Added for navigation buttons

const buttonText = computed(() => 'Next')

const fetchQRCode = async () => {
  const testId = route.params.testId
  if (!testId) return
  try {
    const response = await axios.get(`/tests/${testId}/questions/generate-qr-code`, { responseType: 'arraybuffer' })
    const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ''))
    qrCode.value = `data:image/png;base64,${base64}`
  } catch (error) {
    console.error('Error fetching QR code:', error)
  }
}

const goToText = () => {
  router.push({ name: 'QuestionsText', params: { testId: route.params.testId }, query: route.query })
}
const goToUpload = () => {
  router.push({ name: 'QuestionsUpload', params: { testId: route.params.testId }, query: route.query })
}

const goBack = () => {
  router.go(-1)
}

const triggerNext = async () => {
  // Optional: Add validation if needed
  if (overview.value && overview.value.pages.length === 0) {
    if (!confirm('You have not uploaded any pages via your phone. Are you sure you want to proceed?')) {
      return // Stop execution if user cancels
    }
  }

  // This is a simple navigation step, so no loading state is strictly necessary,
  // but it's good practice for consistency.
  loading.value = true
  router.push({ name: 'AnswersUpload', params: { testId: route.params.testId } })
  loading.value = false
}

onMounted(fetchQRCode)
</script>
